import { useEffect, useId, useRef } from 'react'
import { useChatContext } from './chat-context'
import type { ChatScreenRegistration } from './types'

// getPayload/applyResult are read through a ref so the screen re-registers only
// when `tab` changes, while the chat still reads the freshest state at send time.
export function useRegisterChatContext<TPayload = unknown, TResult = unknown>(
  registration: ChatScreenRegistration<TPayload, TResult>
): void {
  const { registerContext, unregisterContext } = useChatContext()
  const id = useId()
  const registrationRef = useRef(registration)
  registrationRef.current = registration

  const { tab } = registration
  const canApply = registration.applyResult !== undefined

  useEffect(() => {
    const entry: ChatScreenRegistration = {
      tab,
      getPayload: () => registrationRef.current.getPayload()
    }
    // Expose applyResult only when the screen provides one, so consumers can
    // tell an editable screen from a read-only one by its presence.
    if (canApply) {
      entry.applyResult = (result) =>
        registrationRef.current.applyResult?.(result as TResult) ?? false
    }
    registerContext(id, entry)
    return () => unregisterContext(id)
  }, [id, tab, canApply, registerContext, unregisterContext])
}
